import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getMyProfile } from "../api/api";
import { UserProfile } from "../types";

type AuthContextType = {
    user: UserProfile | null;
    isLoading: boolean;
    hasRole: (role: string) => boolean;
    refreshProfile: () => Promise<void>;
    logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const normalizeRoles = (roles: any): string[] => {
    if (!roles) return [];

    if (typeof roles === "string") {
        return [roles];
    }
    if (Array.isArray(roles)) {
        return roles
            .map((r) => {
                if (typeof r === "string") return r;
                if (typeof r === "object" && r !== null) {
                    return r.name ?? r.code ?? r.role ?? "";
                }

                return "";
            })
            .filter(Boolean);
    }
    return [];
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<UserProfile | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const loadProfile = async () => {
        const token = localStorage.getItem("accessToken");
        if (!token) {
            setUser(null);
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            const profile = await getMyProfile();
            setUser(profile);
        } catch (e) {
            localStorage.removeItem("accessToken");
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadProfile();
    }, []);

    const logout = () => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        setUser(null);
        setIsLoading(false);
    };


    const value = useMemo<AuthContextType>(() => {
        const roles = normalizeRoles((user as any)?.roles);

        return {
            user,
            isLoading,
            hasRole: (role: string) =>
                roles.some((r) => r.toUpperCase() === role.toUpperCase()),
            refreshProfile: loadProfile,
            logout,
        };
    }, [user, isLoading]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
